import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { AttachmentsPanel } from "@/components/core/AttachmentsPanel";
import { TIPI, toLocalInput, fromLocalInput } from "@/lib/calendar";

const L = ({ children }) => <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

const initial = (appt, defaults, clientId) => {
  if (appt) return { ...appt, inizio: toLocalInput(appt.inizio), fine: toLocalInput(appt.fine) };
  const s = defaults?.start ? new Date(defaults.start) : new Date(Date.now() + 3600e3);
  if (!defaults?.start || defaults.allDay) s.setHours(defaults?.allDay ? 10 : s.getHours(), 0, 0, 0);
  const e = defaults?.end && !defaults.allDay ? new Date(defaults.end) : new Date(s.getTime() + 3600e3);
  return { titolo: "", client_id: clientId || "", tipo: "riunione", inizio: toLocalInput(s), fine: toLocalInput(e),
    luogo: "", link: "", descrizione: "", partecipanti: defaults?.partecipanti || [], promemoria_cliente: false };
};

export const AppointmentFormDialog = ({ open, onOpenChange, appt, defaults, clientId, clients = [], users = [], onSaved }) => {
  const attRef = useRef(null);
  const [f, setF] = useState(initial(null));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial(appt, defaults, clientId)); }, [open, appt, defaults, clientId]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const toggleSocio = (id) => setF((p) => ({ ...p, partecipanti: p.partecipanti.includes(id) ? p.partecipanti.filter((x) => x !== id) : [...p.partecipanti, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.titolo.trim()) return toast.error("Inserisci un titolo");
    if (!f.client_id) return toast.error("Seleziona un cliente");
    if (!f.inizio || !f.fine || new Date(f.fine) <= new Date(f.inizio)) return toast.error("L'orario di fine deve essere successivo all'inizio");
    setSaving(true);
    const body = { client_id: f.client_id, titolo: f.titolo, tipo: f.tipo, inizio: fromLocalInput(f.inizio), fine: fromLocalInput(f.fine),
      luogo: f.luogo, link: f.link, descrizione: f.descrizione, partecipanti: f.partecipanti, promemoria_cliente: !!f.promemoria_cliente };
    try {
      const r = appt ? await api.put(`/appointments/${appt.id}`, body) : await api.post("/appointments", body);
      await attRef.current?.flush(r.data.id);
      toast.success(appt ? "Appuntamento aggiornato" : "Appuntamento creato");
      onOpenChange(false);
      onSaved?.(r.data, !appt);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl core-scroll" data-testid="appointment-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{appt ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><L>Titolo *</L><Input value={f.titolo} onChange={set("titolo")} className="rounded-xl" data-testid="appt-form-titolo" /></div>
          <div className="grid grid-cols-2 gap-4">
            {!clientId && (
              <div className="space-y-1.5"><L>Cliente *</L>
                <Select value={f.client_id} onValueChange={set("client_id")}>
                  <SelectTrigger className="rounded-xl" data-testid="appt-form-cliente"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><L>Tipo</L>
              <Select value={f.tipo} onValueChange={set("tipo")}>
                <SelectTrigger className="rounded-xl" data-testid="appt-form-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><L>Inizio *</L><Input type="datetime-local" value={f.inizio} onChange={set("inizio")} className="rounded-xl" data-testid="appt-form-inizio" /></div>
            <div className="space-y-1.5"><L>Fine *</L><Input type="datetime-local" value={f.fine} onChange={set("fine")} className="rounded-xl" data-testid="appt-form-fine" /></div>
            <div className="space-y-1.5"><L>Luogo</L><Input value={f.luogo} onChange={set("luogo")} placeholder="Indirizzo o sala" className="rounded-xl" data-testid="appt-form-luogo" /></div>
            <div className="space-y-1.5"><L>Link videochiamata</L><Input value={f.link} onChange={set("link")} placeholder="https://meet.google.com/..." className="rounded-xl" data-testid="appt-form-link" /></div>
          </div>
          <div className="space-y-1.5"><L>Descrizione</L><Textarea rows={3} value={f.descrizione} onChange={set("descrizione")} className="rounded-xl" data-testid="appt-form-descrizione" /></div>
          <AttachmentsPanel ref={attRef} tipo="appointment" targetId={appt?.id} testid="appt-attachments" />
          <div className="space-y-2"><L>Soci partecipanti</L>
            <div className="flex flex-wrap gap-2">
              {users.filter((u) => u.is_active || f.partecipanti.includes(u.id)).map((u) => (
                <label key={u.id} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold cursor-pointer transition-colors ${f.partecipanti.includes(u.id) ? "bg-[#A6DAFC] border-[#0A1128]/30 text-[#0A1128]" : "bg-[#A6DAFC] border-slate-200 text-slate-600"}`}>
                  <Checkbox checked={f.partecipanti.includes(u.id)} onCheckedChange={() => toggleSocio(u.id)} data-testid={`appt-form-socio-${u.id}`} />
                  {u.nome} {u.cognome}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center justify-between gap-4 rounded-xl bg-[#A6DAFC] px-4 py-3 cursor-pointer">
            <span>
              <span className="block text-sm font-bold text-[#0A1128]">Promemoria al cliente il giorno prima</span>
              <span className="block text-xs text-slate-600">Email automatica alle 10:00 del giorno precedente ai destinatari dell'invito (o all'email del cliente).</span>
            </span>
            <Switch checked={!!f.promemoria_cliente} onCheckedChange={set("promemoria_cliente")} className="data-[state=checked]:bg-[#E8173A] data-[state=unchecked]:bg-[#5FB3EA]" data-testid="appt-form-promemoria-cliente" />
          </label>
          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="appt-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6" data-testid="appt-form-submit">{saving ? "Salvataggio..." : appt ? "Salva modifiche" : "Crea appuntamento"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
